"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { adminAPI, handleApiError } from "../../../services/api";
import EInvoiceReportingManager from "./EInvoiceReportingManager";
import "./InvoiceProfileManager.css";

const COUNTRIES = [
  { code: "JO", label: "Jordan" },
  { code: "SA", label: "Saudi Arabia" },
];

const CURRENCY_BY_COUNTRY = { JO: "JOD", SA: "SAR" };

// Supported e-invoicing countries (Jordan / Saudi Arabia)
const SUPPORTED_COUNTRIES = { JO: true, SA: true };

/**
 * Country-specific e-invoicing requirement branches. Disabled for now — the
 * structure is prepared here so turning the integration on is a config change,
 * not a UI rebuild. Field lists follow the JoFotara / ZATCA gap analysis.
 */
const COUNTRY_BRANCHES = {
  JO: {
    title: "Jordan — JoFotara (e-invoicing)",
    fields: [
      { key: "client_id", label: "JoFotara Client ID" },
      { key: "secret_key", label: "JoFotara Secret Key", secret: true },
      { key: "tax_number", label: "Clinic Tax / National Number" },
      { key: "invoice_type", label: "Invoice Type (income 011/021 vs sales 012/022)" },
      // JoFotara (UBL) requires the seller's postal address on every invoice
      { key: "seller_street", label: "Seller Street Name" },
      { key: "seller_building_number", label: "Seller Building Number" },
      { key: "seller_city", label: "Seller City" },
      { key: "seller_governorate", label: "Seller Governorate" },
      { key: "seller_postal_code", label: "Seller Postal Code" },
    ],
  },
  SA: {
    title: "Saudi Arabia — ZATCA (Fatoora)",
    fields: [
      { key: "vat_number", label: "VAT Registration Number (15 digits)" },
      { key: "csid", label: "Compliance CSID" },
      { key: "production_csid", label: "Production CSID" },
      { key: "pih", label: "Previous Invoice Hash (auto-tracked)" },
      // ZATCA UBL requires the seller's postal address on every invoice
      { key: "seller_street", label: "Seller Street Name" },
      { key: "seller_building_number", label: "Seller Building Number" },
      { key: "seller_district", label: "Seller District / Neighborhood" },
      { key: "seller_city", label: "Seller City" },
      { key: "seller_province", label: "Seller Province / Region" },
      { key: "seller_postal_code", label: "Seller Postal Code" },
    ],
  },
};

const InvoiceProfileManager = () => {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language?.startsWith("ar");

  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // per-clinic draft edits + dirty tracking
  const [drafts, setDrafts] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [view, setView] = useState("profiles"); // profiles | reporting
  const requestSeq = useRef(0);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(handler);
  }, [search]);

  const load = useCallback(async (page = 1) => {
    const seq = ++requestSeq.current;
    setIsLoading(true);
    setError(null);
    try {
      const response = await adminAPI.getInvoiceProfiles({
        page,
        per_page: 15,
        search: debouncedSearch || undefined,
      });
      if (seq !== requestSeq.current) return;
      const data = response?.data?.data || {};
      setItems(data.items || []);
      setPagination(data.pagination || null);
      setDrafts({});
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setError(handleApiError(err));
    } finally {
      if (seq === requestSeq.current) setIsLoading(false);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    load(currentPage);
  }, [load, currentPage]);

  const getDraft = (item) => {
    if (drafts[item.clinic_id]) return drafts[item.clinic_id];
    const p = item.profile;
    // Prefill the invoicing country from the clinic's own country (chosen at
    // clinic creation) when the profile doesn't set one yet. Only JO/SA are
    // supported by the e-invoicing branches.
    const clinicCode = item.clinic_country_code || "";
    const countryCode =
      p?.country_code || (SUPPORTED_COUNTRIES[clinicCode] ? clinicCode : "");
    return {
      country_code: countryCode,
      currency: p?.currency || CURRENCY_BY_COUNTRY[countryCode] || "",
      invoice_prefix: p?.invoice_prefix || "",
      next_invoice_number: p?.next_invoice_number ?? 1,
      country_settings: p?.country_settings || {},
    };
  };

  const updateDraft = (clinicId, patch) => {
    setDrafts((prev) => {
      const item = items.find((i) => i.clinic_id === clinicId);
      const current = prev[clinicId] || getDraft(item);
      const next = { ...current, ...patch };
      // Picking a country auto-fills the currency unless explicitly changed
      if (patch.country_code !== undefined) {
        next.currency = CURRENCY_BY_COUNTRY[patch.country_code] || "";
      }
      return { ...prev, [clinicId]: next };
    });
    setSaveError(null);
  };

  const isDirty = (item) => {
    const draft = drafts[item.clinic_id];
    if (!draft) return false;
    const saved = item.profile;
    return (
      (draft.country_code || "") !== (saved?.country_code || "") ||
      (draft.currency || "") !== (saved?.currency || "") ||
      (draft.invoice_prefix || "") !== (saved?.invoice_prefix || "") ||
      Number(draft.next_invoice_number) !== Number(saved?.next_invoice_number ?? 1) ||
      JSON.stringify(draft.country_settings || {}) !== JSON.stringify(saved?.country_settings || {})
    );
  };

  // Nested country_settings edits merge into the clinic's draft
  const updateCountrySetting = (clinicId, key, value) => {
    const item = items.find((i) => i.clinic_id === clinicId);
    if (!item) return;
    const draft = getDraft(item);
    updateDraft(clinicId, {
      country_settings: { ...(draft.country_settings || {}), [key]: value },
    });
  };

  // Master enable/disable pill — persists immediately, independent of the
  // draft/save flow. Nothing clinic-facing reads this flag yet; it's stored
  // now so the rollout later is a flip, not a migration.
  const handleToggle = async (item) => {
    const next = !(item.profile?.enabled ?? false);
    setTogglingId(item.clinic_id);
    setSaveError(null);
    try {
      await adminAPI.updateInvoiceProfile(item.clinic_id, { enabled: next });
      await load(currentPage);
    } catch (err) {
      setSaveError(handleApiError(err));
    } finally {
      setTogglingId(null);
    }
  };

  const handleSave = async (item) => {    const draft = getDraft(item);
    setSavingId(item.clinic_id);
    setSaveError(null);
    try {
      await adminAPI.updateInvoiceProfile(item.clinic_id, {
        country_code: draft.country_code || null,
        currency: draft.currency || null,
        invoice_prefix: draft.invoice_prefix,
        next_invoice_number: Number(draft.next_invoice_number) || 1,
        country_settings: draft.country_settings || {},
      });
      await load(currentPage);
    } catch (err) {
      setSaveError(handleApiError(err));
    } finally {
      setSavingId(null);
    }
  };

  const branch = (countryCode) => (countryCode ? COUNTRY_BRANCHES[countryCode] : null);

  return (
    <div className="invoice-manager">
      <div className="invoice-manager-header">
        <div>
          <h2 className="invoice-manager-title">
            {t("invoiceAdmin.title", "Invoicing")}
          </h2>
          <p className="invoice-manager-subtitle">
            {t(
              "invoiceAdmin.subtitle",
              "Base invoice settings per clinic. Country-specific e-invoicing (Jordan / Saudi Arabia) is prepared below and activates later."
            )}
          </p>
        </div>
        <input
          type="text"
          className="invoice-manager-search"
          placeholder={t("invoiceAdmin.searchPlaceholder", "Search clinics...")}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setCurrentPage(1);
          }}
        />
      </div>

      <div className="invoice-manager-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={view === "profiles"}
          className={`invoice-manager-tab${view === "profiles" ? " active" : ""}`}
          onClick={() => setView("profiles")}
        >
          {t("invoiceAdmin.tabs.profiles", "Clinic profiles")}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={view === "reporting"}
          className={`invoice-manager-tab${view === "reporting" ? " active" : ""}`}
          onClick={() => setView("reporting")}
        >
          {t("invoiceAdmin.tabs.reporting", "Reporting")}
        </button>
      </div>

      {view === "reporting" ? (
        <EInvoiceReportingManager />
      ) : (
        <>
      {error && <div className="invoice-manager-error">{error}</div>}
      {saveError && <div className="invoice-manager-error">{saveError}</div>}

      <div className="invoice-manager-table-wrap">
        <table className="invoice-manager-table">
          <thead>
            <tr>
              <th>{t("invoiceAdmin.table.clinic", "Clinic")}</th>
              <th>{t("invoiceAdmin.table.country", "Country")}</th>
              <th>{t("invoiceAdmin.table.currency", "Currency")}</th>
              <th>{t("invoiceAdmin.table.prefix", "Prefix")}</th>
              <th>{t("invoiceAdmin.table.nextNumber", "Next #")}</th>
              <th>{t("invoiceAdmin.table.status", "Status")}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={7} className="invoice-manager-loading">
                  {t("common.loading", "Loading...")}
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={7} className="invoice-manager-loading">
                  {t("invoiceAdmin.table.empty", "No clinics found.")}
                </td>
              </tr>
            ) : (
              items.map((item) => {
                const draft = getDraft(item);
                const dirty = isDirty(item);
                const open = expanded === item.clinic_id;
                const br = branch(draft.country_code);
                return [
                  <tr key={item.clinic_id} className={`invoice-manager-row${open ? " open" : ""}`}>
                    <td className="invoice-manager-clinic">
                      <button
                        type="button"
                        className="invoice-manager-expand"
                        onClick={() => setExpanded(open ? null : item.clinic_id)}
                        aria-label={t("invoiceAdmin.expand", "Country requirements")}
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          style={{ transform: open ? "rotate(90deg)" : "none" }}
                        >
                          <polyline points="9 18 15 12 9 6"></polyline>
                        </svg>
                      </button>
                      <span className="invoice-manager-clinic-name">
                        {isAr && item.clinic_name_ar ? item.clinic_name_ar : item.clinic_name_en}
                      </span>
                      {item.profile === null && (
                        <span className="invoice-manager-badge new">
                          {t("invoiceAdmin.badge.notConfigured", "not set up")}
                        </span>
                      )}
                      {item.profile !== null && item.readiness?.ready && (
                        <span className="invoice-manager-badge ready">
                          {t("invoiceAdmin.badge.ready", "Ready")}
                        </span>
                      )}
                      {item.profile !== null && !item.readiness?.ready && item.readiness?.qr_ready && (
                        <span className="invoice-manager-badge ready">
                          {t("invoiceAdmin.badge.qrReady", "QR ready")}
                        </span>
                      )}
                      {item.profile !== null && !item.readiness?.ready && !item.readiness?.qr_ready && (
                        <span
                          className="invoice-manager-badge missing"
                          title={item.readiness?.missing?.join(" · ")}
                        >
                          {t("invoiceAdmin.badge.missing", "Missing")}: {item.readiness?.missing?.length ?? 0}
                        </span>
                      )}
                    </td>
                    <td>
                      <select
                        className="invoice-manager-input"
                        value={draft.country_code}
                        onChange={(e) => updateDraft(item.clinic_id, { country_code: e.target.value })}
                      >
                        <option value="">
                          {t("invoiceAdmin.country.none", "— select —")}
                        </option>
                        {COUNTRIES.map((c) => (
                          <option key={c.code} value={c.code}>
                            {c.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <select
                        className="invoice-manager-input"
                        value={draft.currency}
                        onChange={(e) => updateDraft(item.clinic_id, { currency: e.target.value })}
                      >
                        <option value="">{t("invoiceAdmin.currency.none", "—")}</option>
                        <option value="JOD">JOD</option>
                        <option value="SAR">SAR</option>
                      </select>
                    </td>
                    <td>
                      <input
                        type="text"
                        className="invoice-manager-input narrow"
                        value={draft.invoice_prefix}
                        maxLength={20}
                        placeholder="SUL-"
                        onChange={(e) => updateDraft(item.clinic_id, { invoice_prefix: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        className="invoice-manager-input narrow"
                        min={1}
                        value={draft.next_invoice_number}
                        onChange={(e) =>
                          updateDraft(item.clinic_id, { next_invoice_number: e.target.value })
                        }
                      />
                    </td>
                    <td className="invoice-manager-actions">
                      <button
                        type="button"
                        className={`invoice-manager-pill${item.profile?.enabled ? " on" : ""}`}
                        disabled={
                          togglingId === item.clinic_id ||
                          (!(item.profile?.enabled ?? false) && !item.readiness?.ready)
                        }
                        onClick={() => handleToggle(item)}
                        title={
                          !(item.profile?.enabled ?? false) && !item.readiness?.ready
                            ? `${t("invoiceAdmin.pill.blocked", "Fill all required fields to enable")}: ${item.readiness?.missing?.join(", ")}`
                            : t("invoiceAdmin.pill.hint", "Toggle invoicing for this clinic (stored, activates with the feature)")
                        }
                      >
                        <span className="invoice-manager-pill-knob" />
                        <span className="invoice-manager-pill-label">
                          {item.profile?.enabled
                            ? t("invoiceAdmin.status.enabled", "Enabled")
                            : t("invoiceAdmin.status.disabled", "Disabled")}
                        </span>
                      </button>
                    </td>
                    <td className="invoice-manager-actions">
                      <button
                        type="button"
                        className="invoice-manager-save"
                        disabled={!dirty || savingId === item.clinic_id}
                        onClick={() => handleSave(item)}
                      >
                        {savingId === item.clinic_id
                          ? t("common.saving", "Saving...")
                          : t("common.save", "Save")}
                      </button>
                    </td>
                  </tr>,
                  open ? (
                    <tr key={`${item.clinic_id}-branch`} className="invoice-manager-branch-row">
                      <td colSpan={7}>
                        {br ? (
                          <div className="invoice-manager-branch">
                            <div className="invoice-manager-branch-header">
                              <h4>{br.title}</h4>
                              <span className="invoice-manager-badge soon">
                                {t("invoiceAdmin.badge.stored", "Saved per clinic")}
                              </span>
                            </div>
                            <p className="invoice-manager-branch-note">
                              {draft.country_code === "SA"
                                ? t(
                                    "invoiceAdmin.branch.noteSA",
                                    "Invoice-subscribed Saudi clinics get the ZATCA QR code on their invoices. Seller address + VAT number feed the QR."
                                  )
                                : t(
                                    "invoiceAdmin.branch.note",
                                    "These fields are saved per clinic and activated when e-invoicing goes live for this country."
                                  )}
                            </p>
                            <div className="invoice-manager-branch-grid">
                              {br.fields.map((f) => (
                                <div key={f.key} className="invoice-manager-branch-field">
                                  <label>{f.label}</label>
                                  <input
                                    type={f.secret ? "password" : "text"}
                                    value={draft.country_settings?.[f.key] || ""}
                                    onChange={(e) =>
                                      updateCountrySetting(item.clinic_id, f.key, e.target.value)
                                    }
                                    autoComplete="off"
                                    {...(f.secret ? { autoComplete: "new-password" } : {})}
                                    placeholder={t("invoiceAdmin.branch.placeholder", "Pending")}
                                  />
                                </div>
                              ))}
                            </div>
                            {item.readiness?.missing?.length > 0 && (
                              <p className="invoice-manager-missing">
                                <strong>{t("invoiceAdmin.branch.stillMissing", "Still missing")}:</strong>{" "}
                                {item.readiness.missing.join(" · ")}
                              </p>
                            )}
                          </div>
                        ) : (
                          <div className="invoice-manager-branch empty">
                            {t(
                              "invoiceAdmin.branch.pickCountry",
                              "Pick a country above to see its invoicing requirements."
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ) : null,
                ];
              })
            )}
          </tbody>
        </table>
      </div>

      {pagination && pagination.last_page > 1 && (
        <div className="invoice-manager-pagination">
          <button
            type="button"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => p - 1)}
          >
            ←
          </button>
          <span>
            {pagination.current_page} / {pagination.last_page}
          </span>
          <button
            type="button"
            disabled={currentPage >= pagination.last_page}
            onClick={() => setCurrentPage((p) => p + 1)}
          >
            →
          </button>
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default InvoiceProfileManager;
